import { useAuthenticatedWebSocket } from "@/libs/web-sockets";
import { NotificationRegistryRead } from "@/services/generatedApi";
import { NotificationList } from "@/ui-components/components/UserMenu/components/NotificationList";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import { Badge, IconButton, Popover, Tooltip } from "@mui/material";
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";

type NotificationResponse =
  | {
      type: "notification.active";
      notifications: NotificationRegistryRead[];
    }
  | {
      type: "notification.dismissed";
      dismissed: number;
    }
  | {
      type: "notification.new";
      notification: NotificationRegistryRead;
    };

export const NotificationButton = () => {
  const { t } = useTranslation();
  const [anchorElNotification, setAnchorElNotification] = React.useState<null | HTMLElement>(null);
  const { sendJsonMessage, lastMessage } = useAuthenticatedWebSocket(
    `${import.meta.env.VITE_WS_URL}/ws/notifications`,
    {
      share: true,
    },
  );
  const [notifications, setNotifications] = React.useState<NotificationRegistryRead[]>([]);

  const handleOpenNotificationMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElNotification(event.currentTarget);
  };

  const handleCloseNotificationMenu = () => {
    setAnchorElNotification(null);
  };

  const markAsRead = (id: number) => {
    sendJsonMessage({ type: "dismiss_notification", dismiss: id });
  };

  const markAllAsRead = () => {
    notifications.forEach(notification => {
      if (notification.dismissed_at === null) {
        markAsRead(notification.id);
      }
    });
  };

  useEffect(() => {
    if (lastMessage !== null) {
      const data = JSON.parse(lastMessage.data) as NotificationResponse;
      if (data.type === "notification.active") {
        setNotifications(data.notifications);
      }
      if (data.type === "notification.dismissed") {
        setNotifications(prev =>
          prev.map(notification => {
            if (notification.id === data.dismissed) {
              return {
                ...notification,
                dismissed_at: new Date().toISOString(),
              };
            }
            return notification;
          }),
        );
      }
      if (data.type === "notification.new") {
        setNotifications(prev => [data.notification, ...prev]);
      }
    }
  }, [lastMessage]);

  return (
    <>
      <Tooltip title={t("general.Notifications")}>
        <IconButton
          sx={{
            height: "36px",
            width: "36px",
            borderRadius: "8px",
            p: 0,
            "&:hover": {
              backgroundColor: "#536059",
            },
          }}
          onClick={handleOpenNotificationMenu}
        >
          <Badge
            badgeContent={
              notifications?.filter(
                ({ dismissed_at, notification_body }) =>
                  !dismissed_at && notification_body.event_type === "Application",
              ).length ?? 0
            }
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "right",
            }}
            sx={{
              "& .MuiBadge-badge": {
                backgroundColor: "var(--error-color)",
                color: "var(--soft-black-gray-scale-100-white)",
              },
            }}
          >
            <NotificationsNoneIcon sx={{ color: "#FFFFFF", width: "34px", height: "34px" }} />
          </Badge>
        </IconButton>
      </Tooltip>
      <Popover
        id="notifications-popover"
        open={Boolean(anchorElNotification)}
        anchorEl={anchorElNotification}
        onClose={handleCloseNotificationMenu}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        slotProps={{
          paper: {
            sx: { width: "600px", height: "470px", borderRadius: "16px" },
          },
        }}
      >
        <NotificationList notifications={notifications} markAsRead={markAsRead} markAllAsRead={markAllAsRead} />
      </Popover>
    </>
  );
};
